// src/components/ProjectDetail.js
import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { fetchProjectById, updateProject, deleteProject } from '../services/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // プロジェクトデータの取得
  useEffect(() => {
    const loadProject = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const response = await fetchProjectById(id);
        if (response.data) {
          setProject(response.data);
          setEditData(response.data);
          setError(null);
        } else {
          setError('プロジェクトの読み込みに失敗しました');
          navigate('/');
        }
      } catch (err) {
        setError(err.message || 'プロジェクトの読み込みに失敗しました');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id, navigate]);

  // 編集モードの切り替え
  const toggleEditMode = () => {
    if (isEditing) {
      // 編集モードキャンセル時は元のデータに戻す
      setEditData(project);
    }
    setIsEditing(!isEditing);
  };

  // 編集データの変更処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  // データ保存処理
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // データベース用にフィールド名を変換
      const apiData = {
        summary: editData.summary,
        details: editData.details,
        assignee: editData.assignee,
        clientName: editData.client_name,
        budget: editData.budget,
        schedule: editData.schedule,
      };

      await updateProject(id, apiData);
      
      // 更新成功後、表示データを更新
      setProject({
        ...project,
        ...editData
      });
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err.message || '更新に失敗しました');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // 日付のフォーマット
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP');
  };

  if (loading && !project) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="inline-block animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <p className="ml-2">読み込み中...</p>
      </div>
    );
  }

  // コンポーネント内で削除関数を追加
  const handleDelete = async () => {
    // 削除前の確認ダイアログ
    if (!window.confirm('このプロジェクトを削除してもよろしいですか？')) {
      return;
    }
    
    setLoading(true);
    try {
      await deleteProject(id);
      // 削除成功後、一覧ページにリダイレクト
      navigate('/');
    } catch (err) {
      setError(err.message || 'プロジェクトの削除に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex justify-between items-center">
        <Link to="/" className="text-blue-500 hover:text-blue-700">
          ← 一覧に戻る
        </Link>
        
        <button
          onClick={toggleEditMode}
          className={`px-4 py-2 rounded ${
            isEditing 
              ? 'bg-gray-500 hover:bg-gray-600 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          {isEditing ? 'キャンセル' : '編集'}
        </button>

        {!isEditing && (
          <button
            onClick={handleDelete}
            className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white"
          >
            削除
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {project && (
        <div>
          {/* 上部情報セクション */}
          <div className="bg-white p-6 rounded shadow-md mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-lg font-bold mb-2">相談内容（要約）</h2>
                {isEditing ? (
                  <textarea
                    name="summary"
                    value={editData.summary || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded"
                    rows="3"
                  />
                ) : (
                  <p className="text-gray-700">{project.summary || '(未設定)'}</p>
                )}
              </div>
              
              <div>
                <h2 className="text-lg font-bold mb-2">担当者</h2>
                {isEditing ? (
                  <input
                    type="text"
                    name="assignee"
                    value={editData.assignee || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                ) : (
                  <p className="text-gray-700">{project.assignee}</p>
                )}
              </div>
              
              <div>
                <h2 className="text-lg font-bold mb-2">クライアント名</h2>
                {isEditing ? (
                  <input
                    type="text"
                    name="client_name"
                    value={editData.client_name || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                ) : (
                  <p className="text-gray-700">{project.client_name}</p>
                )}
              </div>
              
              <div>
                <h2 className="text-lg font-bold mb-2">概算予算</h2>
                {isEditing ? (
                  <input
                    type="text"
                    name="budget"
                    value={editData.budget || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                ) : (
                  <p className="text-gray-700">{project.budget || '(未設定)'}</p>
                )}
              </div>
              
              <div>
                <h2 className="text-lg font-bold mb-2">プロジェクト期間</h2>
                {isEditing ? (
                  <input
                    type="text"
                    name="schedule"
                    value={editData.schedule || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded"
                  />
                ) : (
                  <p className="text-gray-700">{project.schedule || '(未設定)'}</p>
                )}
              </div>
              
              <div>
                <h2 className="text-lg font-bold mb-2">相談日時</h2>
                <p className="text-gray-700">{formatDate(project.created_at)}</p>
              </div>
            </div>
            
            {isEditing && (
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="mt-6 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded w-full disabled:bg-gray-400"
              >
                {isSaving ? (
                  <>
                    <span className="inline-block animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                    保存中...
                  </>
                ) : '保存'}
              </button>
            )}
          </div>
          
          {/* 相談内容整理セクション */}
          <div className="bg-white p-6 rounded shadow-md">
            <h2 className="text-xl font-bold mb-4">相談内容整理</h2>
            {isEditing ? (
              <textarea
                name="details"
                value={editData.details || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded"
                rows="20"
              />
            ) : (
              <div className="prose max-w-none whitespace-pre-wrap">
                {project.details || '(未設定)'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;